import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { withTheme } from "../../../themes/theming";
import '../index.css';
import BootstrapTable from 'react-bootstrap-table-next';
import FilterDropdown from "../widgets/filterComponent"
import { fetchScorePerSubquestion } from "../../../actions/dashboardAction";
import { AUTO_DEFAULT_FILTER } from "../../../AppConstant";

const ScoreTable = (props) => {
    var I18n = require("react-redux-i18n").I18n;
    const { customHeight, dispatch, dealerOperations } = props;
    const [filterStatus, setFilterStatus] = useState(I18n.t('dashboardElements.All'))
    const [hasFetched, setHasFetched] = useState(false);
    const [data, setData] = useState([]);

    const handleApplyFilters = (filters) => {
        setFilterStatus(filters[0]);
    };

    /** Memoize filtered data for performance and sorting */
    const filteredData = useMemo(() => {
        let filtered = data;
        if (filterStatus === I18n.t('dashboardElements.Critical')) {
            filtered = data?.filter(item => item.critical === true);
        } else if (filterStatus === I18n.t('dashboardElements.NonCritical')) {
            filtered = data?.filter(item => item.critical === false);
        }
        // Sort by compliantDealerPercentage in ascending order (lowest first)
        return filtered?.sort((a, b) => a.compliantDealerPercentage - b.compliantDealerPercentage);
    }, [data, filterStatus]);


    /** Handler for API success callback */
    const onSuccess = (response) => setData(response);

    /** Handler for API failure callback */
    const onFailure = (error) => console.error("❌ Error while fetching list of video", error);

    const fetchProgressBarData = () => {
        try {
            if (!props.toolboxByType?.toolboxByType || props.toolboxByType.toolboxByType?.length === 0) {
                return;
            }

            const toolBoxList = props.toolboxByType?.toolboxByType;
            const cycleList = props.cycleDataList;

            if (!toolBoxList || toolBoxList?.length === 0 || !cycleList || cycleList?.length === 0) {
                console.warn("Required data is not ready yet.");
                return;
            }

            const initialToolBoxId = toolBoxList[toolBoxList?.length - 1];
            const initialCycle = cycleList[cycleList?.length - 1];

            if (!initialCycle?.cycle) {
                console.warn("Initial cycle format is missing.");
                return;
            }
            const [initialStartDate, initialEndDate] = initialCycle.cycle.split(" to ");
            const nmscCode = props.existingProps.userMdl.nmsc;
            const activeProgram = props.existingProps.userMdl.activeProgram;

            const data = {
                "userNmscCode": props.existingProps.userMdl.nmsc,
                "userLangCode": props.existingProps.userMdl.lang,
                "programList": [props.existingProps.userMdl.activeProgram],
                "nmscCode": props.existingProps.nmscFlag ? [nmscCode.toUpperCase()] : props.existingProps.nmscFilter,
                "brand": props.existingProps.brandFilter,
                "country": props.existingProps.countryFilter,
                "region": props.existingProps.regionFilter,
                "userId": props.existingProps.userMdl.username,
                "businessArea": props.existingProps.userMdl.businessAreaKey,
                "toolboxIDList": (props.existingProps.toolboxIDList?.length > 0) ? props.existingProps.toolboxIDList : [Number(initialToolBoxId?.packageMapPrimaryKey)],
                "startDate": props.existingProps.startDate !== "" ? props.existingProps.startDate : initialStartDate,
                "endDate": props.existingProps.endDate !== "" ? props.existingProps.endDate : initialEndDate,
                "dealerOperations": dealerOperations?.length > 0 ? dealerOperations : (AUTO_DEFAULT_FILTER[activeProgram] || ['All'])
            }
            dispatch(fetchScorePerSubquestion(onSuccess, onFailure, data))
        } catch (error) {
            console.error("Error fetching progress bar data:", error);
        }
    }


    useEffect(() => {
        const toolBoxReady = props.toolboxByType?.toolboxByType?.length > 0;
        const cycleReady = props.cycleDataList?.length > 0;

        if (toolBoxReady && cycleReady && !hasFetched) {
            fetchProgressBarData();
            setHasFetched(true);
        }
    }, [props.toolboxByType?.toolboxByType, props.cycleDataList, hasFetched]);

    const isCriticalFormatter = (cell, row) => {
        if (row?.critical) {
            return <span className="text-danger"><i className="fa fa-exclamation-triangle" aria-hidden="true"></i></span>;
        }
    };

    const percentageFormatter = (cell, row) => {
        return cell ? `${cell}%` : '0%';
    };

    const columns = [{
        dataField: 'seqNum',
        text: I18n.t('Table.no'),
        sort: true,
        style: { width: "3%", textAlign: "center" },
        headerStyle: { width: "3%", textAlign: "center", paddingLeft: "2px" },
    }, {
        dataField: 'critical',
        formatter: isCriticalFormatter,
        headerStyle: { width: "2.2%", textAlign: "left" }
    }, {
        dataField: 'chapterName',
        text: I18n.t('Table.chapterName'),
        title: I18n.t('Table.chapterName'),
        sort: true,
        formatter: null,
        style: { width: "10.5%", textAlign: "left", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" },
        headerStyle: { width: "9.3%", textAlign: "left",  wordWrap: "break-word" }
    }, {
        dataField: 'subQuestionText',
        text: I18n.t('Table.subQuestion'),
        title: true,
        sort: true,
        style: { 
            textAlign: "left",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: "1.3em",
            maxHeight: "3.2em",
            wordWrap: "break-word",
            hyphens: "auto"
        },
        headerStyle: { width: "8%", textAlign: "center",  wordWrap: "break-word" },
    }, {
        dataField: 'compliantDealerPercentage',
        text: I18n.t('Table.compliantDealer'),
        sort: true,
        formatter: percentageFormatter,
        style: { width: "7%", textAlign: "center" },
        headerStyle: { width: "7%", textAlign: "center",  wordWrap: "break-word" },
    }];

    return (
        <div className='toolboxImplNasCss' style={{ minWidth: "100%" }}>

            <div className={"large-widget-header left-align-header widget-header-" + props.theme.name} title={I18n.t('dashboardHeaders.ScoreOnSubQuestionsElement')}>
                {I18n.t('dashboard.ScoreOfSubQuestions')}
                <FilterDropdown
                    options={[I18n.t('dashboardElements.All'), I18n.t('dashboardElements.Critical'), I18n.t('dashboardElements.NonCritical')]}
                    onApply={handleApplyFilters}
                    defaultSelected={[I18n.t('dashboardElements.AllAudit')]}
                    theme="dark"
                    icon="filter"
                    width="220px"
                    customClassName="widget-filter-menu-score"
                />

            </div>

            <div >
                {/* Sticky Header Section */}
                <div style={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#fff", // important to avoid transparent background while scrolling
                    zIndex: 1,
                    padding: "10px 15px",
                    borderBottom: "1px solid #ccc",
                }}>
                    <p style={{
                        display: 'inline-block',
                        margin: 0,
                        marginRight: '13px',
                        border: "1px solid rgb(110 98 98)",
                        // padding: "2px 3px",
                        backgroundColor: "rgb(237 180 180)",
                        // fontWeight: 700,
                        width: '6.4rem',
                        paddingInline: '14px',
                        fontFamily: 'Arial, Helvetica, sans-serif',
                        fontSize: '13.5px',
                        display: "flex",
                        justifyContent: "center"
                    }}>
                        {filterStatus}
                    </p>
                </div>
                <div style={{ 
                    height: customHeight ? customHeight : "300px", 
                    overflowY: "auto", 
                    position: "relative"
                }}>
                    <BootstrapTable
                        keyField='actionPlanId'
                        noDataIndication={I18n.t('tabsDetails.noActionAvailable')}
                        data={filteredData || []}
                        columns={columns}
                        hover
                        condensed
                        classes="sticky-header-table"
                        headerClasses="sticky-table-header"
                    //rowEvents={rowEvents}
                    />
                </div>
            </div>
        </div>
    );
}
const mapStateToProps = state => {
    return {
        user: state.loginReducer,
        activityToolBox: state.activityReducer,
        cycleDataList: state.dashboardReducer.cycleDataList,
        toolboxByType: state.leftNavReducer
    };
};
export default withRouter(connect(mapStateToProps)(withTheme(ScoreTable)));

